import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as settingsService from '@/services/settingsService';
import type { FixedSlot } from '@/services/settingsService';

export function SlotsConfig() {
  const [slots, setSlots] = useState<FixedSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getFixedSlots();
      // Sort slots by order
      setSlots(data.sort((a, b) => a.order - b.order));
    } catch (err) {
      console.error(err);
      toast.error('Không thể tải cấu hình khung giờ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const handleAddField = () => {
    const newId = `slot_${Date.now()}`;
    const newSlot: FixedSlot = {
      id: newId,
      name: `Khung giờ mới`,
      startTime: '08:00',
      endTime: '12:00',
      order: slots.length > 0 ? Math.max(...slots.map((s) => s.order)) + 1 : 1,
    };
    setSlots([...slots, newSlot]);
  };

  const handleRemoveField = (id: string) => {
    setSlots(slots.filter((s) => s.id !== id));
  };

  const handleFieldChange = (id: string, key: keyof FixedSlot, value: any) => {
    setSlots(
      slots.map((s) => {
        if (s.id === id) {
          return { ...s, [key]: value };
        }
        return s;
      })
    );
  };

  const handleSave = async () => {
    // Basic validation
    const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
    for (const slot of slots) {
      if (!slot.name.trim()) {
        toast.error('Tên khung giờ không được để trống');
        return;
      }
      if (!timeRegex.test(slot.startTime) || !timeRegex.test(slot.endTime)) {
        toast.error(`Thời gian của slot "${slot.name}" phải ở dạng HH:mm (ví dụ: 09:00)`);
        return;
      }
    }

    try {
      setSaving(true);
      await settingsService.updateFixedSlots(slots);
      toast.success('Đã lưu cấu hình khung giờ thành công!');
      fetchSlots();
    } catch (err) {
      console.error(err);
      toast.error('Lưu cấu hình thất bại');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 bg-card border border-border rounded-xl">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm transition-all duration-300 hover:shadow-md">
      <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Cấu hình Khung giờ cố định</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Định nghĩa các khung giờ dùng cho lịch lưới kiểu BBSHouse. Admin có thể tùy ý sửa đổi giờ bắt đầu/kết thúc.
          </p>
        </div>
        <Button
          onClick={handleAddField}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 hover:bg-zinc-100 hover:text-black transition-all border-zinc-200"
        >
          <Plus className="h-4 w-4" />
          Thêm khung giờ
        </Button>
      </div>

      {slots.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg">
          Chưa cấu hình khung giờ nào. Nhấp vào "Thêm khung giờ" để tạo.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-muted-foreground px-2">
            <div className="col-span-3">Tên khung giờ</div>
            <div className="col-span-3">Giờ bắt đầu</div>
            <div className="col-span-3">Giờ kết thúc</div>
            <div className="col-span-2">Thứ tự hiển thị</div>
            <div className="col-span-1 text-right">Thao tác</div>
          </div>

          {slots.map((slot) => (
            <div
              key={slot.id}
              className="grid grid-cols-12 gap-4 items-center bg-zinc-50/50 dark:bg-zinc-900/50 p-3 rounded-lg border border-border transition-all hover:bg-zinc-50 hover:border-zinc-300 dark:hover:bg-zinc-900 duration-200"
            >
              <div className="col-span-3">
                <Input
                  value={slot.name}
                  onChange={(e) => handleFieldChange(slot.id, 'name', e.target.value)}
                  className="bg-background border-border text-sm"
                  placeholder="Ví dụ: Sáng"
                />
              </div>
              <div className="col-span-3">
                <Input
                  value={slot.startTime}
                  onChange={(e) => handleFieldChange(slot.id, 'startTime', e.target.value)}
                  className="bg-background border-border text-sm"
                  placeholder="09:00"
                />
              </div>
              <div className="col-span-3">
                <Input
                  value={slot.endTime}
                  onChange={(e) => handleFieldChange(slot.id, 'endTime', e.target.value)}
                  className="bg-background border-border text-sm"
                  placeholder="12:00"
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  value={slot.order}
                  onChange={(e) => handleFieldChange(slot.id, 'order', parseInt(e.target.value) || 0)}
                  className="bg-background border-border text-sm"
                />
              </div>
              <div className="col-span-1 text-right">
                <Button
                  onClick={() => handleRemoveField(slot.id)}
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <div className="flex justify-end pt-4 border-t border-border mt-6">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-all font-semibold px-6 py-2.5 rounded-lg"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Đang lưu...' : 'Lưu cấu hình'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
